// @ts-nocheck
/**
 * API Webhooks list — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + row-action DropdownMenu + ConfirmDialog + JSON viewer dialog). Backend
 * contract preserved: API_APIWEBHOOK_LIST / API_APIWEBHOOK_DELETE.
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Plus, Pencil, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import CopyButton from './components/CopyButton';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_APIWEBHOOK_LIST, API_APIWEBHOOK_DELETE } from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

interface JsonModal {
  title: string;
  content: string;
  isJson: boolean;
}

const ApiWebhookList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [webhooksData, setWebhooksData] = useState<any[]>([]);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [jsonModal, setJsonModal] = useState<JsonModal | null>(null);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      const parsed = parseModuleOperations(user.acl_settings.module_operations);
      setLoggedInUserACL(parsed);
      setModuleOperationsForReadPermissionCheck(parsed);
    }
    fetchList();
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchList = () => {
    getApiCall(API_APIWEBHOOK_LIST, {})
      .then((resp: any) => setWebhooksData(resp ?? []))
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
          toast.error(friendlyApiError(err) || 'Session expired');
        } else {
          console.log(err);
        }
      })
      .finally(() => setLoading(false));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    postApiCall(API_APIWEBHOOK_DELETE, { id: pendingDelete.id })
      .then(() => {
        toast.success('Webhook deleted successfully');
        setPendingDelete(null);
        fetchList();
      })
      .catch((err: any) => toast.error(friendlyApiError(err)))
      .finally(() => setDeleting(false));
  };

  const openJson = (content: any, title: string, isJson = false) => {
    const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
    setJsonModal({ title, content: contentStr ?? '', isJson });
  };

  const formatJson = (jsonString: string) => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'api-webhooks',
        data: webhooksData,
        columns: [
          { header: 'Webhook Id', accessor: 'id' },
          { header: 'Webhook Name', accessor: (row: any) => row.webhook_name || '-' },
          {
            header: 'Payload',
            accessor: (row: any) => {
              const c = row.api_text || row.payload;
              return c ? (typeof c === 'string' ? c : JSON.stringify(c)) : '-';
            },
          },
          { header: 'Response Parameters', accessor: (row: any) => row.response_parameters || '-' },
          { header: 'Fixed JSON', accessor: (row: any) => row.fixed_json || '-' },
          {
            header: 'Headers',
            accessor: (row: any) =>
              row.header
                ? typeof row.header === 'string'
                  ? row.header
                  : JSON.stringify(row.header)
                : '-',
          },
          { header: 'Webhook URL', accessor: (row: any) => row.url || '-' },
          { header: 'Request Type', accessor: (row: any) => row.request_type || '-' },
          {
            header: 'Request Date',
            accessor: (row: any) =>
              row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-',
          },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const isCallCentreAdmin = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.API_WEBHOOK,
    'read',
  );
  const canAdd =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.API_WEBHOOK, 'add');
  const canEdit =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.API_WEBHOOK, 'edit');
  const canDelete =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.API_WEBHOOK, 'delete');
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'webhook_name',
        header: 'Name',
        cell: ({ row }) =>
          canEdit ? (
            <Link
              to={`/${ROUTES.APIWEBHOOK.UPDATE.replace(':id', row.original.id)}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.original.webhook_name}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{row.original.webhook_name}</span>
          ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.url ? (
            <button
              type="button"
              onClick={() => openJson(row.original.url, 'URL', false)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary/10 px-2.5 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="View URL"
            >
              <Eye className="h-3.5 w-3.5" />
              View
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'request_type',
        header: 'Method',
        size: 100,
        cell: ({ row }) => {
          const m = String(row.original.request_type || '').toUpperCase();
          if (!m) return <span className="text-xs text-muted-foreground">—</span>;
          const tone =
            m === 'GET'
              ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
              : m === 'POST'
              ? 'bg-primary/10 text-primary ring-primary/25'
              : m === 'PUT' || m === 'PATCH'
              ? 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300'
              : m === 'DELETE'
              ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
              : 'bg-muted text-muted-foreground ring-border/60';
          return (
            <span
              className={
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                tone
              }
            >
              {m}
            </span>
          );
        },
      },
      {
        id: 'payload',
        header: 'Payload',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => {
          const content = row.original.api_text || row.original.payload;
          if (!content) return <span className="text-xs text-muted-foreground">—</span>;
          const isJson = !!row.original.payload && !row.original.api_text;
          return (
            <button
              type="button"
              onClick={() => openJson(content, 'Payload', isJson)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary/10 px-2.5 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="View payload"
            >
              <Eye className="h-3.5 w-3.5" />
              View
            </button>
          );
        },
      },
      {
        id: 'response_parameters',
        header: 'Response Params',
        size: 130,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.response_parameters ? (
            <button
              type="button"
              onClick={() =>
                openJson(row.original.response_parameters, 'Response Parameters', true)
              }
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary/10 px-2.5 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="View response params"
            >
              <Eye className="h-3.5 w-3.5" />
              View
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'fixed_json',
        header: 'Fixed JSON',
        size: 110,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.fixed_json ? (
            <button
              type="button"
              onClick={() => openJson(row.original.fixed_json, 'Fixed JSON', true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary/10 px-2.5 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="View fixed JSON"
            >
              <Eye className="h-3.5 w-3.5" />
              View
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'header',
        header: 'Headers',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.header ? (
            <button
              type="button"
              onClick={() => openJson(row.original.header, 'Headers', true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary/10 px-2.5 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="View headers"
            >
              <Eye className="h-3.5 w-3.5" />
              View
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) =>
          row.original.created_at ? (
            <span className="text-xs text-muted-foreground">
              {new Date(row.original.created_at).toLocaleString()}
            </span>
          ) : (
            '—'
          ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/${ROUTES.APIWEBHOOK.UPDATE.replace(':id', row.original.id)}`);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="Edit"
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete(row.original);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                title="Delete"
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [showActions, canEdit, canDelete, isSuperAdmin, loggedInUserACL, navigate]);

  if (loading) return <Loader />;

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="API" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }


  return (
    <FillPage>
      <PageHeader
        title="API Webhook"
        description="Outbound HTTP endpoints called on campaign / delivery events. Test payloads before saving."
        actions={
          <>
            {webhooksData.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
                className="gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button asChild>
                <Link to={`/${ROUTES.APIWEBHOOK.CREATE}`}>
                  <Plus className="h-4 w-4" />
                  Add API Webhook
                </Link>
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="api-webhooks:list:v1"
        columns={columns}
        data={webhooksData}
        isLoading={loading}
        searchPlaceholder="Search webhooks by name…"
        emptyTitle="No API webhooks yet"
        emptyDescription="Add a webhook to start receiving HTTP callbacks for API events."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        exportFilename="api-webhooks.csv"
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this webhook?"
        description={
          <>
            Webhook{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.webhook_name}
            </span>{' '}
            will be permanently deleted. Any campaigns firing this webhook will stop delivering
            to it.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />

      {/* JSON viewer dialog */}
      <Dialog open={jsonModal !== null} onOpenChange={(o) => !o && setJsonModal(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{jsonModal?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end">
            <CopyButton text={jsonModal?.content ?? ''} />
          </div>
          <div
            className="overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap break-words"
            style={{ maxHeight: '60vh' }}
          >
            {jsonModal?.isJson ? formatJson(jsonModal.content) : jsonModal?.content}
          </div>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default ApiWebhookList;
